import _                                                                    from "underscore";
import React, { useEffect, useRef, useState }                               from "react";
import { Checkbox, Divider, Form, Icon, Menu, Segment, SemanticICONS, Tab } from "semantic-ui-react";
import { Formik, FormikProps }                                              from "formik";
import * as Yup                                                             from "yup";
import { useApolloClient, useLazyQuery, useQuery }                          from "@apollo/react-hooks";
import { Cell, Grid }                                                       from "styled-css-grid";

import { formatOption, RouterUtility, runIfFunction }                                                                                                         from "@src/util";
import { SystemAccountingQueries, UserQueries }                                                                                                               from "@src/queries";
import { Align, CorvalProject, CorvalProjectAccount, CorvalProjectRole, CorvalProjectTemplate, RouterLocationParams, SemanticUIColor, Size, Uuid }            from "@src/types";
import { queriesHaveData, queriesLoading }                                                                                                                    from "@src/store/_apolloUtil";
import { StyledContainer }                                                                                                                                    from "@src/views/containers";
import { FieldDescription, FieldWrapper, MultipleInputArrayField, OrganizationalMembersDropdown, ProjectRoleDropdown, SelectField, TextAreaField, TextField } from "@src/views/components/forms";
import { QueryDialogProps, QueryOpDialog }                                                                                                                    from "@src/views/components/from-query/QueryOpDialog";
import { BasicDialogContainer }                                                                                                                               from "@src/views/components/modals/BasicDialogContainer";
import { LoadingIndicator }                                                                                                                                   from "@src/views/components/util/loading/LoadingIndicator";
import { TagField, TagFieldDefaultProps }                                                                                                                     from "@src/views/components/forms/formik/TagField";
import { useCurrentUser, useCustomFormikHelpers }                                                                                                             from "@src/hooks";
import { ENTITY_ICONS, ORGANIZATIONAL_ROLES, PROJECT_ROLES }                                                                                                  from "@src/constants";
import { FieldLabel }                                                                                                                                         from "@src/views/components/forms/formik/FieldLabel";
import { IconMenu }                                                                                                                                           from "@src/views/components/menus/IconMenu";
import { UseIf }                                                                                                                                              from "@src/views/components/display/EqualValues";
import { ProjectVersionDropdown }                                                                                                                             from '@src/views/components/forms/dropdowns/ProjectVersionDropdown';
import { FormTabWithNotifier }                                                                                                                                from '@src/views/components/display/ItemWithNotifier';
import { WarningMessage }                                                                                                                                     from '@src/views/components/util/messaging/WarningMessage';
import { useLocation, useRouteMatch }                                                                                                                         from 'react-router-dom';
import { FinePrintMessage }                                                                                                                                   from '@src/views/components/util/messaging/FinePrintMessage';


interface ProjectTemplateFormValues {
    templateId: string,
    templateName: string,
    templateDescription: string,
    templateCategory: string,
    templateIcon: string,
    templateTags: string[],
    templateOrgIds: string[],
    templateArchived: boolean,
    templateClonesProject: boolean,
    templateProjectId: Uuid,
    templateProjectVersionId: Uuid,
    templateProjectMembers: Pick<CorvalProjectAccount, 'accountId' | 'projectRole'>[],
    templateProjectOrgId: Uuid,
}

const initialValues: ProjectTemplateFormValues = {
    templateId:               "",
    templateName:             "",
    templateDescription:      "",
    templateCategory:         "Generic",
    templateIcon:             ENTITY_ICONS.PROJECTS.TEMPLATES.ENTITY,
    templateTags:             [],
    templateOrgIds:           [],
    templateArchived:         false,
    templateClonesProject:    true,
    templateProjectId:        null,
    templateProjectVersionId: null,
    templateProjectMembers:   [],
    templateProjectOrgId:     null,
};

interface ProjectTemplateManagementDialogProps extends QueryDialogProps {
    templateId?: string,
    projectId?: Uuid,
    projectVersionId?: Uuid,
    onComplete?: ( data: { id: string, name: string } ) => void,
    allowProjectConversion?: boolean,
}

const projectTemplateIconSet = [
    ENTITY_ICONS.PROJECTS.TEMPLATES.TYPES.FULLY_PACKAGED,
    ENTITY_ICONS.PROJECTS.TEMPLATES.TYPES.GLOBALLY_FOCUSED,
    ENTITY_ICONS.PROJECTS.TEMPLATES.TYPES.RESOURCE_FOCUSED,
    ENTITY_ICONS.PROJECTS.TEMPLATES.TYPES.BUDGET_FOCUSED,
    ENTITY_ICONS.PROJECTS.TEMPLATES.TYPES.TACTICAL,
    ENTITY_ICONS.PROJECTS.TEMPLATES.TYPES.PRECISION,
    ENTITY_ICONS.PROJECTS.TEMPLATES.TYPES.STRUCTURED,
    ENTITY_ICONS.PROJECTS.TEMPLATES.TYPES.TIME_SENSITIVE,
    ENTITY_ICONS.PROJECTS.TEMPLATES.TYPES.TARGETED,
    ENTITY_ICONS.PROJECTS.TEMPLATES.TYPES.EXPERIMENTAL,
    ENTITY_ICONS.PROJECTS.TEMPLATES.TYPES.LOCKED,
    ENTITY_ICONS.PROJECTS.TEMPLATES.TYPES.DO_NOT_USE,
];

const ProjectTemplateManagementDialogDefaultProps = {
    templateId:             null,
    projectId:              null,
    projectVersionId:       null,
    onComplete:             null,
    allowProjectConversion: false,
} satisfies Partial<ProjectTemplateManagementDialogProps>;

function ProjectTemplateManagementDialog( _props: ProjectTemplateManagementDialogProps ){

    const props = { ...ProjectTemplateManagementDialogDefaultProps, ..._props };

    const location = useLocation(),
          match    = useRouteMatch<RouterLocationParams.ProjectSpecificPage>();

    const { projectId, projectVersionId, templateId, onComplete, allowProjectConversion, ...queryOpDialogProps } = props,
          { currentUserReq, currentUser, isSystemAdmin, hasOrganizationalRole }                                  = useCurrentUser(),
          apolloClient                                                                                           = useApolloClient(),
          [ templateListing, setTemplateListing ]                                                                = useState( null ),
          [ userAccessibleOrgs, setUserAccessibleOrgs ]                                                          = useState( [] ),
          [ addedTemplateCategories, setAddedTemplateCategories ]                                                = useState( [] ),
          [ isInSourceProjectContext, setIsInSourceProjectContext ]                                              = useState( false ),
          [ sourceProject, setSourceProject ]                                                                    = useState<CorvalProject | null>( null ),
          { includesErrorsIn }                                                                                   = useCustomFormikHelpers(),
          formikRef                                                                                              = useRef<FormikProps<ProjectTemplateFormValues>>( null );


    if( _.isNull( templateId ) &&
        _.isNull( projectId ) &&
        _.isNull( projectVersionId ) ){
        throw new Error( "ProjectTemplateManagementDialog: Invalid id(s) - project and project version id, or template id, must be provided." );
    }

    const isNewTemplate = ( _.isNull( templateId ) );

    const getOrganizationsQuery   = SystemAccountingQueries.queries.GET_ORGANIZATIONS( SystemAccountingQueries.fragments.ORGANIZATIONS.EDIT_FIELDS ),
          getProjectListingQuery  = SystemAccountingQueries.queries.GET_PROJECT( `id organizationId projectMembers{ accountId projectRole } isTemplateSource` ),
          getTemplateListingQuery = SystemAccountingQueries.queries.GET_PROJECT_TEMPLATES();

    const getOrganizationsReq                       = useQuery( getOrganizationsQuery, { fetchPolicy: "no-cache", skip: !isSystemAdmin() } ),
          getTemplateListingReq                     = useQuery<{ me: { projectTemplates: CorvalProjectTemplate[] } }>( getTemplateListingQuery, { fetchPolicy: "no-cache" } ),
          [ getSourceProject, getSourceProjectReq ] = useLazyQuery<{ projects: CorvalProject[] }>( getProjectListingQuery, { fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true } );

    const isLoading = queriesLoading( [
        getOrganizationsReq,
        getTemplateListingReq
    ] );

    useEffect( () => {

        if( !queriesLoading( getTemplateListingReq ) &&
            queriesHaveData( getTemplateListingReq ) ){

            // set source to the given projectId, if available
            let sourceProjectId = projectId;

            // If we are pulling info from a template...
            if( !isNewTemplate ){

                // Get the source project ID if available from the template listings
                const templatesSourceProjectId = _.findWhere( getTemplateListingReq.data?.me.projectTemplates, { id: templateId } )?.sourceProjectId;

                if( templatesSourceProjectId ){

                    // Save the template's source project id to retrieve details on
                    sourceProjectId = templatesSourceProjectId;

                    // Check if we're operating on the template from within that project's context
                    const userIsInSourceProject = RouterUtility.isCurrentURLProjectSpecific( location.pathname, sourceProjectId );

                    setIsInSourceProjectContext( userIsInSourceProject );
                }
            }

            // Get further details about the source project
            getSourceProject( { variables: { id: sourceProjectId } } );

            setTemplateListing( getTemplateListingReq.data?.me.projectTemplates ?? [] );
        }

    }, [ getTemplateListingReq ] );

    useEffect( () => {

        if( !queriesLoading( getSourceProjectReq ) &&
            queriesHaveData( getSourceProjectReq ) ){

            setSourceProject( _.first( getSourceProjectReq.data?.projects ) );
        }

    }, [ getSourceProjectReq ] );

    useEffect( () => {

        if( !queriesLoading( getOrganizationsReq ) &&
            queriesHaveData( getOrganizationsReq ) ){

            let userOrganizations = [];

            // TODO curate for template authors
            if( isSystemAdmin() ){

                userOrganizations = getOrganizationsReq.data.organizations.map( org => ( {
                    id:      org.id,
                    name:    org.name,
                    members: org.organizationMembers
                } ) );

            } else {

                userOrganizations = currentUser
                    .organizations
                    .filter( userOrg => hasOrganizationalRole( userOrg.organizationId, ORGANIZATIONAL_ROLES.ADMIN ) )
                    .map( userOrg => ( {
                        id:      userOrg.organizationId,
                        name:    userOrg.organization.name,
                        members: userOrg.organization.organizationMembers
                    } ) );

            }

            setUserAccessibleOrgs( userOrganizations );
        }

    }, [ getOrganizationsReq ] );



    const validationSchema = Yup.object().shape( {
        templateName:        Yup.string()
                                .test( "unique name", "Template names already exists.", ( value: string ) => {

                                    // Check against known names for duplication
                                    // If not new, remove the existing template name from consideration
                                    const templatesWithNames = isNewTemplate ? templateListing : ( templateListing ?? [] ).filter( t => t.id !== templateId );

                                    // Format and collect trimmed strings
                                    const existingTemplateNames = [ ..._.map( templatesWithNames ?? [], item => item?.name?.toLowerCase().trim() ) ];

                                    const hasExistingNamesToCompare = ( existingTemplateNames.length !== 0 ),
                                          isNewNameInExistingNames  = existingTemplateNames.includes( value?.toLowerCase().trim() ?? "" );

                                    return !hasExistingNamesToCompare || !isNewNameInExistingNames;
                                } )
                                .min( 10, "Template name must be a minimum of 10 characters" )
                                .required( "Template name is required" ),
        templateDescription: Yup.string().required( "Template description is required" ),
        templateTags:        Yup.array().of( Yup.string().min( 3, "Template tags must be a minimum of 3 characters" ) ),
        templateOrgIds:      Yup.array().of( Yup.string() ),
        templateCategory:    Yup.string().required( "Category choice is required" ),
        templateIcon:        Yup.string().required( "Icon choice is required" )
    } );

    const mutationOperation = isNewTemplate
                              ? SystemAccountingQueries.mutations.CREATE_PROJECT_TEMPLATE()
                              : SystemAccountingQueries.mutations.UPDATE_PROJECT_TEMPLATE();

    const getMutationGraphArgs = () => {

        const { values } = formikRef.current;

        let payload: object = {
            organizationIds:  values.templateOrgIds,
            icon:             values.templateIcon.trim(),
            name:             values.templateName.trim(),
            description:      values.templateDescription.trim(),
            category:         values.templateCategory.trim(),
            tags:             values.templateTags.map( tag => tag.trim() ),
            projectVersionId: values.templateProjectVersionId,
            projectMembers:   values.templateProjectMembers
        };

        if( isNewTemplate ){

            payload = {
                projectId: projectId,
                ...payload,
                cloneSourceProject: values.templateClonesProject
            };

        } else {

            payload = {
                id:         templateId,
                isArchived: values.templateArchived,
                ...payload,
            };
        }

        return ( {
            variables: {
                input: payload
            }
        } );

    };

    const getTemplateFormData = ( templateId?: Uuid ) => {

        const defaultAuthorMember = {
            accountId:   currentUser.id,
            projectRole: PROJECT_ROLES.ADMIN as unknown as CorvalProjectRole
        };

        let template = {
            ...initialValues,
            templateProjectId:        projectId,
            templateProjectVersionId: projectVersionId,
            templateProjectMembers:   [ defaultAuthorMember ],
            templateProjectOrgId:     null,
        };

        if( _.isEmpty( templateId ) || !queriesHaveData( [ getTemplateListingReq ] ) ){
            return template;
        }

        const projectTemplate = _.findWhere( getTemplateListingReq.data?.me.projectTemplates ?? [], { id: templateId } ) as CorvalProjectTemplate;

        return ( projectTemplate ? {
            templateId:               projectTemplate.id,
            templateName:             projectTemplate.name,
            templateDescription:      projectTemplate.description,
            templateTags:             _.pluck( projectTemplate.tags, "tag" ),
            templateOrgIds:           _.pluck( projectTemplate.organizations, "organizationId" ),
            templateCategory:         projectTemplate.category,
            templateIcon:             projectTemplate.icon,
            templateArchived:         projectTemplate.isArchived,
            templateClonesProject:    true,
            templateProjectId:        projectTemplate.sourceProjectId,
            templateProjectVersionId: projectTemplate.sourceProjectVersionId,
            templateProjectMembers:   projectTemplate.project.projectMembers,
            templateProjectOrgId:     projectTemplate.project.organizationId,
        } : template ) as ProjectTemplateFormValues;
    };

    const handleOnComplete = () => {

        runIfFunction( onComplete, {
                name: formikRef.current.values.templateName,
                id:   formikRef.current.values.templateId
            }
        );
    };

    return (

        <Formik<ProjectTemplateFormValues>
            innerRef={ formikRef }
            initialValues={ getTemplateFormData( templateId ) }
            isInitialValid={ false }
            enableReinitialize={ true }
            validateOnChange={ true }
            validationSchema={ validationSchema }
            onSubmit={ null }>

            { formik => {

                let hasDetailErrors = includesErrorsIn( formik, 'templateName', 'templateDescription', 'templateProjectVersionId', 'templateIcon' ),
                    hasMetaErrors   = includesErrorsIn( formik, 'templateCategory', 'templateTags' ),
                    hasAccessErrors = includesErrorsIn( formik, 'templateProjectMembers' );

                return (

                    <QueryOpDialog
                        className={ "no-padding" }
                        { ...queryOpDialogProps }
                        operationDisabled={ ( isNewTemplate && !projectId ) || isLoading || !formik.isValid }
                        onComplete={ handleOnComplete }
                        mutation={ mutationOperation }
                        graphArgs={ getMutationGraphArgs }
                        mutationOptions={ {
                            onCompleted:         async ( data ) => {

                                await getTemplateListingReq.refetch();
                            },
                            awaitRefetchQueries: true,
                            update:              async ( store, results ) => {

                                // if project template has the current user as an author member, update current user and data
                                if( formik.values.templateProjectMembers.map( ( { accountId } ) => accountId ).includes( currentUser.id ) ){

                                    const userData = await apolloClient.query( {
                                        query:       UserQueries.queries.GET_CURRENT_USER(),
                                        variables:   currentUserReq.variables,
                                        fetchPolicy: 'network-only',
                                    } );
                                }
                            }
                        } }
                        submitLabel={ isNewTemplate ? "Create Template" : "Update" }
                        successToastLabel={
                            isNewTemplate
                            ? "Project Template Created!"
                            : "Project Template Updated!"
                        }
                        overflow={ true }
                        scrolling={ false }
                        alignControls={ Align.CENTER }>

                        <Form>

                            <BasicDialogContainer
                                title={ isNewTemplate ? "Create Template from Source Project" : "Update Project Template" }
                                contentPadding={ Size.MD }
                                overflow={ true }
                                scrolling={ true }
                                roundedTop={ true }
                                contentMinHeight={ `50dvh` }
                                contentMaxHeight={ `50dvh` }>

                                <UseIf x={ isLoading } eq={ true }>

                                    <StyledContainer padding={ Size.LG }>
                                        <LoadingIndicator label={ isNewTemplate ? "Preparing Template Data..." : "Loading Template Details..." } isActive={ isLoading } inverted/>
                                    </StyledContainer>

                                </UseIf>
                                <UseIf x={ isLoading } eq={ false }>

                                    <UseIf x={ formik.values.templateArchived } eq={ true }>

                                        <WarningMessage icon={ <Icon name={ 'warning circle' } fitted/> }>This project is archived and is not accessible by users.</WarningMessage>

                                    </UseIf>

                                    <Tab defaultActiveIndex={ 0 }
                                         menu={ { secondary: true, pointing: true } }
                                         panes={ [
                                             {
                                                 menuItem: <Menu.Item key={ 'details' }><FormTabWithNotifier label={ 'Details' } active={ hasDetailErrors }/></Menu.Item>,
                                                 render:   () => (
                                                     <Tab.Pane className={ 'no-border' }>

                                                         <Grid columns={ 1 } rowGap={ "20px" } className={ 'padding-md-horizontal' }>
                                                             <Cell>

                                                                 <FieldWrapper>
                                                                     <FieldLabel>Template Name</FieldLabel>
                                                                     <FieldDescription className={ "margin-xs-bottom" }>Choose a unique name and icon combination to help your team identify this template.</FieldDescription>
                                                                     <Grid columns={ `1fr auto` } areas={ [ "name iconSelect" ] }>
                                                                         <Cell area={ "name" }>

                                                                             <TextField
                                                                                 name={ "templateName" }
                                                                                 autoFocus={ true }
                                                                                 size={ "big" }
                                                                             />

                                                                         </Cell>
                                                                         <Cell area={ "iconSelect" }>

                                                                             <IconMenu
                                                                                 gridSize={ 3 }
                                                                                 cellWidth={ 50 }
                                                                                 iconOptions={ projectTemplateIconSet }
                                                                                 triggerButtonProps={ {
                                                                                     style: { height: 48, width: 60 }
                                                                                 } }
                                                                                 dropdownProps={ {
                                                                                     direction:     "left",
                                                                                     closeOnEscape: true,
                                                                                     closeOnBlur:   true
                                                                                 } }
                                                                                 value={ formik.values.templateIcon as SemanticICONS }
                                                                                 onClick={ value => formik.setFieldValue( "templateIcon", value ) }
                                                                             />

                                                                         </Cell>
                                                                     </Grid>

                                                                 </FieldWrapper>

                                                             </Cell>
                                                             <Cell>

                                                                 <FieldWrapper>
                                                                     <FieldLabel>Description</FieldLabel>
                                                                     <FieldDescription className={ "margin-xs-bottom" }>Describe the template, including purpose and overview of included content.</FieldDescription>
                                                                     <TextAreaField
                                                                         rows={ 3 }
                                                                         name={ "templateDescription" }
                                                                         icon={ "paragraph" }
                                                                         iconPosition={ "left" }
                                                                     />
                                                                 </FieldWrapper>

                                                             </Cell>
                                                             <Cell>

                                                                 <FieldWrapper>
                                                                     <FieldLabel>Source Version</FieldLabel>
                                                                     <FieldDescription className={ "margin-xs-bottom" }>The version of the template to use when creating new projects for users. Template authors can manage the versions in the project itself.</FieldDescription>
                                                                     <ProjectVersionDropdown
                                                                         search
                                                                         name={ "templateProjectVersionId" }
                                                                         loading={ isLoading }
                                                                         projectId={ formik.values.templateProjectId }
                                                                     />
                                                                 </FieldWrapper>

                                                             </Cell>

                                                             { ( isNewTemplate && allowProjectConversion ) && (
                                                                 <Cell>

                                                                     <Divider/>

                                                                     <FieldWrapper>
                                                                         <FieldLabel>Creation Method</FieldLabel>
                                                                         <FieldDescription>
                                                                             <>When saving as a new project, creating a template duplicates the source project.</>
                                                                             { !sourceProject?.isTemplateSource && <>When converting, the source project becomes the new template.</> }
                                                                         </FieldDescription>

                                                                         <Grid columns={ 3 }>
                                                                             <Cell>

                                                                                 <Checkbox fitted={ true }
                                                                                           radio={ true }
                                                                                           id={ 'templateClonesProject' }
                                                                                           name={ 'templateClonesProject' }
                                                                                           checked={ formik.values.templateClonesProject }
                                                                                           label={ `Save as new project` }
                                                                                           disabled={ sourceProject?.isTemplateSource }
                                                                                           onClick={ ( e, data ) => {
                                                                                               formik.setFieldValue( 'templateClonesProject', data.checked );
                                                                                           } }
                                                                                 />

                                                                             </Cell>
                                                                             <Cell>

                                                                                 { !sourceProject?.isTemplateSource && (
                                                                                     <Checkbox fitted={ true }
                                                                                               radio={ true }
                                                                                               id={ 'templateAssignsProject' }
                                                                                               name={ 'templateClonesProject' }
                                                                                               checked={ !formik.values.templateClonesProject }
                                                                                               label={ `Use this project` }
                                                                                               onClick={ ( e, data ) => {
                                                                                                   formik.setFieldValue( 'templateClonesProject', !data.checked );
                                                                                               } }
                                                                                     />
                                                                                 ) }

                                                                             </Cell>
                                                                         </Grid>

                                                                     </FieldWrapper>

                                                                 </Cell>
                                                             ) }
                                                         </Grid>

                                                     </Tab.Pane>
                                                 )
                                             },
                                             {
                                                 menuItem: <Menu.Item key={ 'meta' }><FormTabWithNotifier label={ 'Meta' } active={ hasMetaErrors }/></Menu.Item>,
                                                 render:   () => (
                                                     <Tab.Pane className={ 'no-border' }>

                                                         <Grid columns={ 1 } rowGap={ "20px" } className={ 'padding-md-horizontal' }>
                                                             <Cell>

                                                                 <FieldWrapper>
                                                                     <FieldLabel>Template Category</FieldLabel>
                                                                     <FieldDescription className={ "margin-xs-bottom" }>You can specify a new category, or use existing an category.</FieldDescription>
                                                                     <SelectField
                                                                         search
                                                                         allowAdditions={ true }
                                                                         name={ "templateCategory" }
                                                                         loading={ isLoading }
                                                                         style={ {
                                                                             width: "50%"
                                                                         } }
                                                                         onAddItem={ ( e, data ) => {
                                                                             setAddedTemplateCategories( [ ...addedTemplateCategories, data.value ] );
                                                                         } }
                                                                         options={
                                                                             _.chain( [
                                                                                 initialValues.templateCategory,
                                                                                 ...addedTemplateCategories,
                                                                                 ..._.pluck( templateListing, "category" ),
                                                                             ] )
                                                                              .uniq()
                                                                              .map( templateCategory => formatOption( templateCategory, templateCategory ) )
                                                                              .sortBy( "label" )
                                                                              .value()
                                                                         }>
                                                                     </SelectField>
                                                                 </FieldWrapper>

                                                             </Cell>
                                                             <Cell>

                                                                 <FieldWrapper>
                                                                     <FieldLabel>{ TagFieldDefaultProps.label }</FieldLabel>
                                                                     <FieldDescription className={ "margin-xs-bottom" }>{ TagFieldDefaultProps.descriptionText }</FieldDescription>
                                                                     <TagField
                                                                         label={ null }
                                                                         descriptionText={ null }
                                                                         name={ "templateTags" }
                                                                         orientation={ "vertical" }
                                                                         reverseOrder={ true }
                                                                         loading={ isLoading }
                                                                     />
                                                                 </FieldWrapper>

                                                             </Cell>
                                                         </Grid>

                                                     </Tab.Pane>
                                                 )
                                             },
                                             {
                                                 menuItem: <Menu.Item key={ 'access' }><FormTabWithNotifier label={ 'Access & Management' } active={ hasAccessErrors }/></Menu.Item>,
                                                 render:   () => (
                                                     <Tab.Pane className={ 'no-border' }>

                                                         <Grid columns={ 1 } rowGap={ "20px" } className={ 'padding-md-horizontal' }>

                                                             { !isNewTemplate && (
                                                                 <Cell>

                                                                     <Segment secondary>

                                                                         <FieldWrapper>
                                                                             <FieldLabel>Availability</FieldLabel>
                                                                             <FieldDescription className={ "margin-xs-bottom" }>When archived, a template hides itself from all users when creating a new project.</FieldDescription>
                                                                             <Checkbox fitted={ true }
                                                                                       id={ 'templateArchived' }
                                                                                       name={ 'templateArchived' }
                                                                                       checked={ formik.values.templateArchived }
                                                                                       label={ `Template is archived` }
                                                                                       onClick={ ( e, data ) => {
                                                                                           formik.setFieldValue( 'templateArchived', data.checked );
                                                                                       } }
                                                                             />
                                                                         </FieldWrapper>

                                                                     </Segment>

                                                                 </Cell>
                                                             ) }


                                                             { !_.isEmpty( userAccessibleOrgs ) && (
                                                                 <Cell className={ 'position--relative z-index--1000' }>

                                                                     <FieldWrapper>
                                                                         <FieldLabel>Template Access</FieldLabel>
                                                                         <FieldDescription className={ "margin-xs-bottom" }>You can choose to save this template globally, or to specific organizations.</FieldDescription>
                                                                         <SelectField
                                                                             search
                                                                             multiple
                                                                             name={ "templateOrgIds" }
                                                                             loading={ isLoading }
                                                                             options={ userAccessibleOrgs.map( org => formatOption( org.id, org.name ) ) }>
                                                                         </SelectField>
                                                                     </FieldWrapper>

                                                                 </Cell>
                                                             ) }

                                                             <Cell>


                                                                 <MultipleInputArrayField<{ accountId: Uuid, projectRole: string }>
                                                                     name={ 'templateProjectMembers' }
                                                                     label={ 'Template Project Members' }
                                                                     descriptionText={ 'You can choose who can update this project template.' }
                                                                     addLabel={ 'Add Member' }
                                                                     objectFactory={ () => ( {
                                                                         accountId:   '',
                                                                         projectRole: ''
                                                                     } ) }
                                                                     removeDisabledOnIndexes={ ( values: Array<{ accountId: Uuid, projectRole: string }> ) => {

                                                                         const currentUserIndexInList = values.findIndex( value => value.accountId === currentUser.id );

                                                                         return [ currentUserIndexInList ];
                                                                     } }
                                                                     headerContainerClasses={ [ 'position--sticky position--top z-index--100 bg--colored-white padding-sm-vertical margin-xs-bottom' ] }
                                                                     renderField={ ( item, index ) => {

                                                                         const isCurrentUserId               = ( item.accountId === currentUser.id ),
                                                                               currentUserIsInProjectContext = isInSourceProjectContext && isCurrentUserId,
                                                                               optionsToOmit                 = _.without( _.pluck( formik.values.templateProjectMembers, 'accountId' ), item.accountId );

                                                                         return (
                                                                             <Grid columns={ 2 }>

                                                                                 <Cell>

                                                                                     <OrganizationalMembersDropdown
                                                                                         compact
                                                                                         search
                                                                                         id={ `org-${ sourceProject.organizationId }` }
                                                                                         organizationId={ sourceProject.organizationId }
                                                                                         omit={ optionsToOmit }
                                                                                         name={ `templateProjectMembers[${ index }].accountId` }
                                                                                         placeholder="Choose..."
                                                                                         includeErrorMessage={ false }
                                                                                         loading={ isLoading }
                                                                                         disabled={ currentUserIsInProjectContext }
                                                                                     />

                                                                                 </Cell>
                                                                                 <Cell>

                                                                                     <ProjectRoleDropdown
                                                                                         compact
                                                                                         name={ `templateProjectMembers[${ index }].projectRole` }
                                                                                         placeholder="Choose..."
                                                                                         includeErrorMessage={ false }
                                                                                         loading={ isLoading }
                                                                                         disabled={ currentUserIsInProjectContext }
                                                                                     />

                                                                                 </Cell>

                                                                                 { currentUserIsInProjectContext && (
                                                                                     <Cell width={ 2 }>
                                                                                         <FinePrintMessage color={ SemanticUIColor.ORANGE } icon={ 'info circle' } className={ 'padding-sm-bottom' }>To remove yourself, manage this template from outside the template project.</FinePrintMessage>
                                                                                     </Cell>
                                                                                 ) }

                                                                             </Grid>

                                                                         );
                                                                     } }/>

                                                             </Cell>

                                                         </Grid>

                                                     </Tab.Pane>
                                                 )
                                             }
                                         ] }>


                                    </Tab>

                                </UseIf>

                            </BasicDialogContainer>
                        </Form>

                    </QueryOpDialog>
                );
            } }

        </Formik>
    );
}

export {
    ProjectTemplateManagementDialog,
    ProjectTemplateManagementDialogDefaultProps,
    type ProjectTemplateManagementDialogProps
};
